export const WORLD_ZOOMS = [
  { id: "near", distance: 18 },
  { id: "tactical", distance: 32 },
  { id: "overview", distance: 58 },
] as const;
export type WorldZoom = (typeof WORLD_ZOOMS)[number]["id"];
export interface WorldGroundBounds { minX: number; minY: number; maxX: number; maxY: number }
export interface WorldViewport { width: number; height: number }
export interface WorldCaption { key: string; x: number; y: number; width: number; height: number }

/** Keep portrait targets in the space between the left margin and the 48px tool rail. */
export function worldCameraFraming(viewport: WorldViewport): { scale: number; offsetX: number } {
  const portrait = viewport.width < 900 && viewport.height > viewport.width;
  const usableWidth = portrait ? Math.max(1, viewport.width - 68) : viewport.width;
  return {
    scale: Math.max(1, 0.8 * viewport.height / viewport.width) * viewport.width / usableWidth,
    offsetX: portrait ? 24 : 0,
  };
}

/** Small hysteresis avoids rebuilding instanced LODs while the wheel settles on a boundary. */
export function worldZoomAt(distance: number, previous: WorldZoom = "tactical"): WorldZoom {
  if (previous === "near" && distance < 26) return "near";
  if (previous === "overview" && distance > 42) return "overview";
  if (distance < 23) return "near";
  if (distance > 46) return "overview";
  return "tactical";
}

export function activeIncoming<T extends { arrivesAt: number }>(incoming: readonly T[], now: number): T[] {
  return incoming.filter((a) => Number.isFinite(a.arrivesAt) && a.arrivesAt > now);
}

/** Keep actionable threat captions clear of the HUD, desktop rail, map tools and mobile navigation. */
export function placeThreatLabel(x: number, y: number, width: number, height: number, viewport: WorldViewport): { x: number; y: number } {
  const desktop = viewport.width >= 900;
  return {
    x: Math.max((desktop ? 104 : 10) + width / 2, Math.min(viewport.width - 58 - width / 2, x)),
    y: Math.max((desktop ? 106 : 142) + height, Math.min(viewport.height - (desktop ? 24 : 96), y)),
  };
}

/** Place the own badge first, then actionable captions, retaining their anchors whenever they fit. */
export function layoutThreatLabels(captions: readonly WorldCaption[], viewport: WorldViewport, obstacles: readonly WorldCaption[] = []): Map<string, { x: number; y: number }> {
  const placed: WorldCaption[] = obstacles.map((obstacle) => ({ ...obstacle }));
  const result = new Map<string, { x: number; y: number }>();
  const gap = 6;
  const overlaps = (a: WorldCaption, b: WorldCaption) => a.x - a.width / 2 < b.x + b.width / 2 + gap && a.x + a.width / 2 + gap > b.x - b.width / 2 && a.y - a.height < b.y + gap && a.y + gap > b.y - b.height;
  for (const caption of captions) {
    const origin = placeThreatLabel(caption.x, caption.y, caption.width, caption.height, viewport);
    const xs = [origin.x, ...placed.flatMap((p) => [p.x - (p.width + caption.width) / 2 - gap, p.x + (p.width + caption.width) / 2 + gap])];
    const ys = [origin.y, ...placed.flatMap((p) => [p.y - p.height - gap, p.y + caption.height + gap])];
    const candidates = xs.flatMap((x) => ys.map((y) => ({ ...caption, ...placeThreatLabel(x, y, caption.width, caption.height, viewport) })));
    candidates.sort((a, b) => Math.hypot(a.x - origin.x, a.y - origin.y) - Math.hypot(b.x - origin.x, b.y - origin.y));
    const point = candidates.find((candidate) => placed.every((p) => !overlaps(candidate, p))) ?? { ...caption, ...origin };
    placed.push(point); result.set(caption.key, { x: point.x, y: point.y });
  }
  return result;
}
